/**
 * Shared cadence step/event utilities.
 *
 * Single source of truth for step numbering, icon mapping, label derivation,
 * graph walking, and branch handling across ALL cadence timeline renderers:
 *
 *   1. CadenceFlowVisualization  (contact-cadences-tab.js)
 *   2. Blueprint steps table     (template-detail-page.js)
 *   3. Recipient timeline        (recipient-detail-panel.js)
 *   4. Review & Publish summary  (create-cadence-page.js)
 *   5. Cadence flow editor       (cadence-flow-editor.js)
 *
 * Every component should import from here instead of defining its own
 * icon maps, label maps, or step-walking logic.
 *
 * STEP LABELS: The backend persists a `stepLabel` field (e.g., "1", "3a") on
 * each event at save time. Components should prefer event.stepLabel over the
 * client-computed value from walkCadenceGraph. Client-side numbering is a
 * fallback for records created before backend step labeling was introduced.
 * See cadence-data-mappers.js mapEventsToDetailSteps for the priority logic.
 */
import { STEP_TYPE_COLORS } from './cadence-constants';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/**
 * All selectable manual task types for the task type picker.
 * `id` = UI key, `actionType` = backend event_config.action_type value.
 */
export const TASK_TYPES = [
  { id: 'email', label: 'Email', icon: 'email', actionType: 'send_email' },
  { id: 'call', label: 'Call', icon: 'call', actionType: 'call' },
  { id: 'sms', label: 'Text Message', icon: 'textsms-message', actionType: 'sms' },
  { id: 'linkedin', label: 'LinkedIn', icon: 'linkedin-box', actionType: 'linkedin' },
  { id: 'print_mail', label: 'Print Mail', icon: 'send', actionType: 'print_mail' },
  { id: 'thankview', label: 'ThankView', icon: 'star', actionType: 'thankview' },
  { id: 'research', label: 'Research', icon: 'search', actionType: 'research' },
  { id: 'other', label: 'Other', icon: 'task', actionType: 'other' },
];

/** Get task type info by id or by backend action_type value. */
export const getTaskTypeInfo = (idOrActionType) => {
  if (!idOrActionType) return TASK_TYPES.find((t) => t.id === 'other');
  const byId = TASK_TYPES.find((t) => t.id === idOrActionType);
  if (byId) return byId;
  // Reverse-lookup by actionType (e.g. 'send_email' → email)
  const byAction = TASK_TYPES.find((t) => t.actionType === idOrActionType);
  return byAction || TASK_TYPES.find((t) => t.id === 'other');
};

/** Canonical action type info keyed by lowercase event type. */
export const ACTION_TYPE_INFO = {
  email: { label: 'Email', icon: 'email' },
  task: { label: 'Manual', icon: 'task' },
  manual: { label: 'Manual', icon: 'task' },
  call: { label: 'Call', icon: 'call' },
  phone: { label: 'Call', icon: 'call' },
  sms: { label: 'Text', icon: 'textsms-message' },
  schedule: { label: 'Schedule', icon: 'schedule' },
  in_person: { label: 'In-person', icon: 'meeting' },
  linkedin: { label: 'LinkedIn', icon: 'linkedin-box' },
  print_mail: { label: 'Print Mail', icon: 'send' },
  thankview: { label: 'ThankView', icon: 'star' },
};

/** Full step type labels (actionable + non-actionable). */
export const STEP_TYPE_LABELS = {
  email: 'Email',
  task: 'Task',
  manual: 'Manual',
  call: 'Call',
  phone: 'Call',
  sms: 'Text',
  condition: 'Condition',
  wait: 'Wait',
  schedule: 'Schedule',
  in_person: 'In-person',
  exit: 'Exit',
  start: 'Start',
  end: 'End',
  linkedin: 'LinkedIn',
  print_mail: 'Print Mail',
  thankview: 'ThankView',
};

/** Event types that should never be shown as numbered user-facing steps. */
export const INTERNAL_EVENT_TYPES = ['start', 'end'];

/** Condition type → display labels for branching UI. */
const CONDITION_LABELS = {
  email_replied: {
    desc: 'Did the contact reply',
    yesHeader: 'Contact replied',
    noHeader: 'No reply',
    yesBadge: 'if replied',
    noBadge: 'if no reply',
  },
  email_opened: {
    desc: 'Did the contact open the email',
    yesHeader: 'Email opened',
    noHeader: 'Not opened',
    yesBadge: 'if opened',
    noBadge: 'if not opened',
  },
  email_clicked: {
    desc: 'Did the contact click a link',
    yesHeader: 'Link clicked',
    noHeader: 'Not clicked',
    yesBadge: 'if clicked',
    noBadge: 'if not clicked',
  },
  interaction_created: {
    desc: 'Was there an interaction',
    yesHeader: 'Interaction recorded',
    noHeader: 'No interaction',
    yesBadge: 'if interaction',
    noBadge: 'if no interaction',
  },
  gift_given: {
    desc: 'Did the contact make a gift',
    yesHeader: 'Gift made',
    noHeader: 'No gift',
    yesBadge: 'if gift made',
    noBadge: 'if no gift',
  },
};

// ---------------------------------------------------------------------------
// Icon / CSS helpers
// ---------------------------------------------------------------------------

/**
 * Material icon name for a step type.
 * Pass optional `taskType` (UI task id or backend action_type) for task steps
 * to get the specific icon rather than the generic assignment icon.
 */
export const getStepIcon = (eventType, taskType) => {
  const t = (eventType || '').toLowerCase();
  // Non-action flow-control steps have fixed icons not in ACTION_TYPE_INFO
  if (t === 'wait') return 'access-time';
  if (t === 'condition') return 'call-split';
  if (t === 'exit') return 'exit-to-app';
  // Task/manual steps: use the task-type-specific icon when available
  if (t === 'task' || t === 'manual') {
    if (taskType) return getTaskTypeInfo(taskType)?.icon || 'task';
    return 'task';
  }
  // All other action types derive from ACTION_TYPE_INFO (single source of truth)
  return ACTION_TYPE_INFO[t]?.icon || 'task';
};

// ---------------------------------------------------------------------------
// Label helpers
// ---------------------------------------------------------------------------

/** { label, icon } for an actionable event type. */
export const getActionTypeInfo = (eventType) => {
  const t = (eventType || '').toLowerCase();
  return ACTION_TYPE_INFO[t] || { label: eventType || 'Other', icon: 'task' };
};

/** Human-readable step name from an event object. */
const getStepLabel = (event) => {
  const cfg = event.eventConfig || event.event_config || {};
  if ((event.eventType || '').toLowerCase() === 'email' && event.isAutomated !== false) {
    return cfg.name || 'Automated Email';
  }
  return cfg.name || cfg.subject || cfg.title || getActionTypeInfo(event.eventType).label;
};

/** Whether a step is automated or manual. Task types always manual. */
const getStepMode = (event) => {
  const t = (event.eventType || '').toLowerCase();
  if (['task', 'manual', 'call', 'phone', 'linkedin', 'print_mail', 'in_person'].includes(t)) return 'Manual';
  return event.isAutomated !== false ? 'Automated' : 'Manual';
};

/** Display labels for a condition event. */
const getConditionLabels = (eventConfig) => {
  const type = eventConfig?.condition_type;
  const match = CONDITION_LABELS[type];
  if (match) return match;
  const name = eventConfig?.name || 'Condition';
  return {
    desc: name,
    yesHeader: 'Condition met',
    noHeader: 'Condition not met',
    yesBadge: 'if true',
    noBadge: 'if false',
  };
};

/**
 * Format a step number with optional branch suffix.
 * PM spec: main steps = "1", "2"; branch steps = "3a", "4b".
 */
export const formatStepNumber = (num, suffix = '') => `${num}${suffix}`;

// ---------------------------------------------------------------------------
// Graph walk — converts events + edges → normalized step list
// ---------------------------------------------------------------------------

/**
 * Walk the cadence event graph to produce a normalized, ordered step structure.
 *
 * Returns { items, actionableCount, conditionCount, hasAutomated, hasManual }
 * where `items` is an ordered array of:
 *   - { type: 'step', num, suffix, event, id, name, actionType, actionIcon, mode, timing, templateName, emailTemplateId, condBadge, condBadgeType }
 *   - { type: 'wait', durationDays, event }
 *   - { type: 'branch', conditionName, afterStepNum, condLabels, event, trueBranch, falseBranch }
 *
 * When edges are empty, falls back to sequenceOrder-based ordering.
 */
export const walkCadenceGraph = (events = [], edges = []) => {
  const empty = { items: [], actionableCount: 0, conditionCount: 0, hasAutomated: false, hasManual: false };
  if (!events.length) return empty;

  // If no edges, fall back to sequenceOrder sort
  if (!edges || !edges.length) {
    return walkBySequenceOrder(events);
  }

  // Build lookup maps
  const eventsById = Object.fromEntries(events.map((e) => [String(e.id), e]));
  const edgesByFrom = {};
  edges.forEach((edge) => {
    const key = String(edge.fromCadenceEventId);
    if (!edgesByFrom[key]) edgesByFrom[key] = [];
    edgesByFrom[key].push(edge);
  });

  const getNextEdge = (eventId) => {
    const out = edgesByFrom[String(eventId)] || [];
    const branchTypes = ['success', 'true_branch', 'failure', 'false_branch'];
    return (
      out.find((e) => (e.edgeType || '').toLowerCase() === 'next') ||
      out.find((e) => !branchTypes.includes((e.edgeType || '').toLowerCase()))
    );
  };

  const getBranchEdge = (eventId, type) => {
    const out = edgesByFrom[String(eventId)] || [];
    const t = type.toLowerCase();
    const altType = t === 'success' ? 'true_branch' : 'false_branch';
    return out.find((e) => {
      const et = (e.edgeType || '').toLowerCase();
      return et === t || et === altType;
    });
  };

  const startEvent = events.find((e) => (e.eventType || '').toLowerCase() === 'start');
  if (!startEvent) return empty;

  let stepNum = 0;
  let conditionCount = 0;
  let hasAutomated = false;
  let hasManual = false;
  const items = [];
  const visited = new Set();

  const makeStepItem = (event, num, suffix, dayNum, condBadge, condBadgeType) => {
    const info = getActionTypeInfo(event.eventType);
    const mode = getStepMode(event);
    if (mode === 'Automated') hasAutomated = true;
    else hasManual = true;
    const taskActionType = event.eventConfig?.action_type;
    const isTaskStep =
      ['task', 'manual'].includes((event.eventType || '').toLowerCase()) ||
      (event.isAutomated === false && !!taskActionType);
    const taskInfo = isTaskStep && taskActionType ? getTaskTypeInfo(taskActionType) : null;
    return {
      type: 'step',
      num,
      suffix,
      event,
      id: event.id,
      name: getStepLabel(event),
      actionType: taskInfo ? taskInfo.label : info.label,
      actionIcon: getStepIcon(event.eventType, taskActionType),
      mode,
      timing: `Day ${dayNum}`,
      templateName: event.eventConfig?.template_name || null,
      emailTemplateId: event.emailTemplateId || event.eventConfig?.email_template_id || null,
      condBadge,
      condBadgeType,
    };
  };

  // Track event IDs consumed by branches so the main walk skips them
  const branchConsumedIds = new Set();

  const walkBranch = (
    conditionEventId,
    edgeType,
    branchBase,
    cumulativeDays,
    suffix,
    badge,
    badgeType,
    mergePointId
  ) => {
    const edge = getBranchEdge(conditionEventId, edgeType);
    if (!edge) return [];
    const steps = [];
    let num = branchBase - 1;
    let days = cumulativeDays;
    let currentId = String(edge.toCadenceEventId);
    const branchVisited = new Set();

    while (currentId && !branchVisited.has(currentId)) {
      // Stop at the merge point — don't consume it or anything after
      if (mergePointId && currentId === mergePointId) break;

      branchVisited.add(currentId);
      branchConsumedIds.add(currentId);
      const event = eventsById[currentId];
      if (!event) break;
      const t = (event.eventType || '').toLowerCase();
      if (t === 'end') break;

      if (t === 'wait') {
        const waitDays = event.eventConfig?.duration_days || 0;
        days += waitDays;
        steps.push({ type: 'wait', durationDays: waitDays, event });
        const next = getNextEdge(event.id);
        currentId = next ? String(next.toCadenceEventId) : null;
        continue;
      }

      if (t === 'start' || t === 'condition') {
        const next = getNextEdge(event.id);
        currentId = next ? String(next.toCadenceEventId) : null;
        continue;
      }

      num++;
      steps.push(makeStepItem(event, num, suffix, days + 1, badge, badgeType));
      const next = getNextEdge(event.id);
      currentId = next ? String(next.toCadenceEventId) : null;
    }

    return steps;
  };

  // Walk main chain from START
  let currentId = null;
  const firstEdge = getNextEdge(startEvent.id);
  if (firstEdge) currentId = String(firstEdge.toCadenceEventId);
  let cumulativeDays = 0;

  while (currentId && !visited.has(currentId)) {
    visited.add(currentId);

    // Skip events already rendered inside a branch (convergence points)
    if (branchConsumedIds.has(currentId)) {
      const next = getNextEdge(currentId);
      currentId = next ? String(next.toCadenceEventId) : null;
      continue;
    }

    const event = eventsById[currentId];
    if (!event) break;
    const t = (event.eventType || '').toLowerCase();
    if (t === 'end') break;

    if (t === 'wait') {
      const days = event.eventConfig?.duration_days || 0;
      items.push({ type: 'wait', durationDays: days, event });
      cumulativeDays += days;
      const next = getNextEdge(event.id);
      currentId = next ? String(next.toCadenceEventId) : null;
      continue;
    }

    if (t === 'condition') {
      conditionCount++;
      const condLabels = getConditionLabels(event.eventConfig);
      const branchBase = stepNum + 1;
      const condEdges = edgesByFrom[String(event.id)] || [];
      // The merge point is where both branches converge — the condition's "next" edge target
      const nextEdge = getNextEdge(event.id);
      const mergePointId = nextEdge ? String(nextEdge.toCadenceEventId) : null;
      const trueBranch = walkBranch(
        event.id,
        'SUCCESS',
        branchBase,
        cumulativeDays,
        'a',
        condLabels.yesBadge,
        'true',
        mergePointId
      );
      const falseBranch = walkBranch(
        event.id,
        'FAILURE',
        branchBase,
        cumulativeDays,
        'b',
        condLabels.noBadge,
        'false',
        mergePointId
      );

      if (trueBranch.length === 0 && falseBranch.length === 0 && condEdges.length > 0) {
        console.warn('[walkCadenceGraph] Condition event', event.id, 'has edges but empty branches. Edges:', condEdges);
      }

      items.push({
        type: 'branch',
        conditionName: condLabels.desc,
        afterStepNum: stepNum,
        condLabels,
        event,
        trueBranch,
        falseBranch,
      });

      // Advance the main-flow counter only by the number of *numbered* steps in
      // the longer branch. Branch arrays also contain wait items, which never get
      // a step number (see walkBranch) — counting them here would over-advance the
      // numbering and skip a number on the post-merge step (SIG-812).
      stepNum += Math.max(getBranchActionSteps(trueBranch).length, getBranchActionSteps(falseBranch).length);
      const next = getNextEdge(event.id);
      currentId = next ? String(next.toCadenceEventId) : null;
      continue;
    }

    stepNum++;
    items.push(makeStepItem(event, stepNum, '', cumulativeDays + 1, null, null));
    const next = getNextEdge(event.id);
    currentId = next ? String(next.toCadenceEventId) : null;
  }

  return { items, actionableCount: stepNum, conditionCount, hasAutomated, hasManual };
};

/**
 * Fallback graph walk when edges are not available.
 * Sorts events by sequenceOrder, filters out START/END/WAIT-only,
 * and produces a flat (no branches) step list.
 */
const walkBySequenceOrder = (events) => {
  let hasAutomated = false;
  let hasManual = false;
  const items = [];
  let stepNum = 0;
  let lastWaitDays = 0;

  const sorted = [...events].sort((a, b) => (a.sequenceOrder || 0) - (b.sequenceOrder || 0));

  for (const event of sorted) {
    const t = (event.eventType || '').toLowerCase();
    if (t === 'start' || t === 'end') continue;

    if (t === 'wait') {
      const days = event.eventConfig?.duration_days || 0;
      items.push({ type: 'wait', durationDays: days, event });
      lastWaitDays += days;
      continue;
    }

    if (t === 'condition') {
      // Without edges we can't resolve branches — show as a note
      const condLabels = getConditionLabels(event.eventConfig);
      items.push({
        type: 'branch',
        conditionName: condLabels.desc,
        afterStepNum: stepNum,
        condLabels,
        event,
        trueBranch: [],
        falseBranch: [],
      });
      continue;
    }

    stepNum++;
    const info = getActionTypeInfo(event.eventType);
    const mode = getStepMode(event);
    if (mode === 'Automated') hasAutomated = true;
    else hasManual = true;
    const taskActionType = event.eventConfig?.action_type;
    const isTaskStep = ['task', 'manual'].includes(t) || (event.isAutomated === false && !!taskActionType);
    const taskInfo = isTaskStep && taskActionType ? getTaskTypeInfo(taskActionType) : null;

    items.push({
      type: 'step',
      num: stepNum,
      suffix: '',
      event,
      id: event.id,
      name: getStepLabel(event),
      actionType: taskInfo ? taskInfo.label : info.label,
      actionIcon: getStepIcon(event.eventType, taskActionType),
      mode,
      timing: `Day ${lastWaitDays + 1}`,
      templateName: event.eventConfig?.template_name || null,
      emailTemplateId: event.emailTemplateId || event.eventConfig?.email_template_id || null,
      condBadge: null,
      condBadgeType: null,
    });
  }

  return { items, actionableCount: stepNum, conditionCount: 0, hasAutomated, hasManual };
};

// ---------------------------------------------------------------------------
// Wait formatting
// ---------------------------------------------------------------------------

/** Format a wait duration for display (e.g., "Wait 7 days"). */
export const formatWaitDuration = (durationDays, durationHours, durationMinutes) => {
  const parts = [];
  if (durationDays) parts.push(`${durationDays} day${durationDays !== 1 ? 's' : ''}`);
  if (durationHours) parts.push(`${durationHours}h`);
  if (durationMinutes) parts.push(`${durationMinutes}m`);
  return parts.length ? `Wait ${parts.join(' ')}` : 'Wait';
};

// ---------------------------------------------------------------------------
// Step label utilities — shared across performance table, recipient panel, and flow visualization
// ---------------------------------------------------------------------------

/** True when a step should be treated as a manual (fundraiser) action. */
export const isManualStep = (step) => step.type === 'task' || step.isAutomated === false;

/** Badge background color for a step based on automation mode and type. */
export const getStepBadgeColor = (step) => {
  if (step.isAutomated === false) return STEP_TYPE_COLORS.task || '#d97706';
  return STEP_TYPE_COLORS[step.type] || '#6b7280';
};

/**
 * Numbered action steps within a condition branch produced by walkCadenceGraph.
 * Branch arrays interleave numbered { type: 'step' } items with non-numbered
 * flow-control items such as { type: 'wait' }. Step-number lists, the
 * performance dropdown, and the blueprint steps table only deal with numbered
 * action steps and must skip wait items — those lack num/suffix/name/actionType
 * and otherwise render as "undefined". (cadence-flow-visualization is the one
 * renderer that intentionally shows waits inside branches and reads the raw array.)
 */
export const getBranchActionSteps = (branch = []) => branch.filter((s) => s.type === 'step');

/**
 * Comma-joined step-number labels for a condition branch (e.g. "3a, 4a"),
 * preferring the backend-persisted event.stepLabel and falling back to
 * client-side numbering. Returns 'none' when the branch has no action steps.
 */
export const getBranchStepLabelList = (branch = []) => {
  const steps = getBranchActionSteps(branch);
  if (!steps.length) return 'none';
  return steps.map((s) => s.event?.stepLabel || formatStepNumber(s.num, s.suffix)).join(', ');
};

/** One-line mode subline: "Automated (ID N)" or "Manual (ID N)". Returns null for wait/unknown or missing ID. */
export const getStepModeSubline = (step) => {
  if (step.type === 'wait') return null;
  const id = step.cadenceEventReferenceNumber;
  if (id == null) return null;
  return isManualStep(step) ? `Manual (ID ${id})` : `Automated (ID ${id})`;
};

// ---------------------------------------------------------------------------
// Email step validation — shared across cadence, blueprint, and prospect plan saves
// ---------------------------------------------------------------------------

/**
 * True when a raw backend events array (Cadence.events, not UI steps) contains at
 * least one AUTOMATED email step. Manual "send email" task events (isAutomated ===
 * false) don't need a verified sending domain, so they must not trip the domain
 * warning gate on activate/publish.
 */
export const hasAutomatedEmailSteps = (events = []) =>
  events.some((e) => (e.eventType || '').toLowerCase() === 'email' && e.isAutomated !== false);

/**
 * Collect all email-type steps from a steps array, including steps nested
 * inside condition branches.
 */
export const collectEmailSteps = (steps = []) => {
  const result = [];
  steps.forEach((s) => {
    if (s.type === 'email') result.push(s);
    if (s.branches) {
      (s.branches.true || []).forEach((bs) => {
        if (bs.type === 'email') result.push(bs);
      });
      (s.branches.false || []).forEach((bs) => {
        if (bs.type === 'email') result.push(bs);
      });
    }
  });
  return result;
};

/**
 * Returns incomplete automated email steps (those missing a subject or body).
 * Pass `requireAutomationEnabled: false` to check ALL email steps regardless of
 * the automationEnabled flag (used by the publish path, which rejects any gap).
 */
export const getIncompleteEmailSteps = (steps = [], { requireAutomationEnabled = true } = {}) => {
  const emailSteps = collectEmailSteps(steps);
  return emailSteps.filter((s) => {
    if (requireAutomationEnabled && !s.automationEnabled) return false;
    return !s.subjectOverride || !s.emailBody;
  });
};

// Backend caps on the manual task generated when a cadence step fires:
// tasks.summary (name) is 255 chars; description is 2000 chars.
export const TASK_STEP_NAME_MAX_LENGTH = 255;
export const TASK_STEP_DESCRIPTION_MAX_LENGTH = 2000;

/**
 * Collect all task-type steps from a steps array, including steps nested inside
 * condition branches.
 */
export const collectTaskSteps = (steps = []) => {
  const result = [];
  steps.forEach((s) => {
    if (s.type === 'task') result.push(s);
    if (s.branches) {
      (s.branches.true || []).forEach((bs) => {
        if (bs.type === 'task') result.push(bs);
      });
      (s.branches.false || []).forEach((bs) => {
        if (bs.type === 'task') result.push(bs);
      });
    }
  });
  return result;
};

/**
 * Returns task steps whose name or description exceeds the backend character
 * limits enforced when the step fires and creates a manual task.
 */
export const getOverlongTaskSteps = (steps = []) => {
  return collectTaskSteps(steps).filter(
    (s) =>
      (s.name && s.name.length > TASK_STEP_NAME_MAX_LENGTH) ||
      (s.description && s.description.length > TASK_STEP_DESCRIPTION_MAX_LENGTH)
  );
};
